import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, LayoutGrid, Loader2, Sparkles, Trash2, Undo2, Wand2, X } from "lucide-react";

import { LAYOUT_RECIPES } from "@dynamic-landing/layout";
import { Button, Card } from "@/ui";
import { cn } from "@/lib/cn";
import { ApiError } from "@/api/client";
import { useWorkspace } from "@/app/WorkspaceContext";
import { useDeleteTheme, useThemes } from "@/data/hooks";
import { themes as themesApi, aiDesign, type Theme } from "@/data/services";
import { ThemeStyleInjector, type ThemeTokens } from "@/editor/theme/ThemeStyleInjector";
import { THEME_PRESETS, type ThemePreset } from "@/editor/theme/themePresets";
import { MANUAL_THEME_PRESETS } from "@/editor/theme/themePresetsManual";
import {
  FONTS,
  FONT_CATEGORY_LABEL,
  fontsForRole,
  fontsForRoleByCategory,
  previewFontsUrl,
  type FontKey,
  type FontRole,
} from "@/editor/theme/fonts";

/**
 * DesignControls — the brand/theme editing surface, extracted from DesignsScreen
 * so it can be reused in BOTH the standalone Designs route AND the Make builder's
 * "Design" tab. It owns its own draft/apply state and talks to the same
 * builder-api themes endpoints; applying activates the theme and invalidates the
 * `active-theme` query, so the Make LivePreview re-themes in place.
 *
 *  - variant="page"  — two-column layout for the full-width Designs screen.
 *  - variant="panel" — single column for the narrow Make right-sidebar tab.
 */

// Dedupe by name so the richer hand-curated style presets (which carry the new
// `design` tokens) WIN over any generated color-only preset of the same name —
// otherwise the gallery shows two "Glassmorphism"/"Editorial"/… with dup keys.
const ALL_PRESETS: ThemePreset[] = (() => {
  const byName = new Map<string, ThemePreset>();
  for (const p of THEME_PRESETS) byName.set(p.name.toLowerCase(), p);
  for (const p of MANUAL_THEME_PRESETS) byName.set(p.name.toLowerCase(), p);
  return [...byName.values()].sort((a, b) => a.name.localeCompare(b.name));
})();

type ColorKey = keyof NonNullable<ThemeTokens["colors"]>;
type ColorField = { key: ColorKey; label: string; hint?: string };
type DesignTokens = NonNullable<ThemeTokens["design"]>;

/** Default design values shown in the Style controls when a draft has none. */
const DESIGN_DEFAULTS: Required<DesignTokens> = {
  radius: "12px",
  shadow: "soft",
  surface: "flat",
  fontScale: 1,
  border: "hairline",
};
/** Parse the numeric px value out of a radius token (for the slider). */
function radiusPx(v: string | undefined): number {
  const n = parseInt(v ?? "", 10);
  return Number.isFinite(n) ? n : 12;
}
const GROUPS: { group: string; fields: ColorField[] }[] = [
  {
    group: "Brand",
    fields: [
      { key: "brand", label: "Brand", hint: "Primary CTA" },
      { key: "brandHover", label: "Brand hover" },
      { key: "onBrand", label: "On brand", hint: "Text on brand" },
    ],
  },
  {
    group: "Accent",
    fields: [
      { key: "accent", label: "Accent", hint: "Eyebrows, links" },
      { key: "onAccent", label: "On accent" },
    ],
  },
  {
    group: "Surface",
    fields: [
      { key: "surface", label: "Surface", hint: "Page background" },
      { key: "onSurface", label: "On surface", hint: "Body text" },
    ],
  },
  {
    group: "Contrast",
    fields: [
      { key: "contrast", label: "Contrast", hint: "Dark hero / footer" },
      { key: "onContrast", label: "On contrast" },
    ],
  },
  {
    group: "State",
    fields: [
      { key: "success", label: "Success" },
      { key: "warning", label: "Warning" },
      { key: "danger", label: "Danger" },
    ],
  },
];

function clone(t: ThemeTokens): ThemeTokens {
  return JSON.parse(JSON.stringify(t));
}
function normHex(v: string | undefined): string {
  return v && /^#[0-9a-fA-F]{6}$/.test(v) ? v : "#000000";
}

export function DesignControls({
  variant = "page",
  onTokensChange,
  onApplyLayout,
  onUndoLayout,
  canUndoLayout = false,
  initialPresetId,
}: {
  variant?: "page" | "panel";
  /** Optional: streams the in-progress draft up as it changes, so a host (the
   *  Make canvas) can preview edits live before "Apply". Absent on the
   *  standalone Designs screen, which leaves behavior unchanged. */
  onTokensChange?: (tokens: ThemeTokens) => void;
  /** Optional: apply the selected design's LAYOUT recipe to the current page
   *  (Make Design tab only — restructures the page tree, undoable). Absent on
   *  the standalone Designs screen (no active page), which shows a hint. */
  onApplyLayout?: (recipeId: string) => void;
  /** Optional: revert the last tree change (the builder's undo). Surfaced as an
   *  explicit button beside Apply-layout so reverting never depends on ⌘Z
   *  keyboard focus (⌘Z is ignored while a text field is focused). */
  onUndoLayout?: () => void;
  /** Whether there's anything to undo (drives the Undo button's disabled state). */
  canUndoLayout?: boolean;
  /** Optional: seed the draft from this preset id on mount (the layout→design
   *  PAIRING — when the Layouts gallery deep-links a layout into Make, its
   *  recommended design is pre-selected & previewed here). */
  initialPresetId?: string;
}) {
  const { currentProjectId, current } = useWorkspace();
  const qc = useQueryClient();
  const themesQ = useThemes(currentProjectId);
  const list = themesQ.data?.items ?? [];
  const activeTheme = list.find((t) => t.is_active) ?? list[0] ?? null;

  // Editable draft + provenance: editing an existing theme (id) or a new one.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ThemeTokens | null>(null);
  const [name, setName] = useState("");
  const [presetId, setPresetId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // Which saved-design chip is awaiting delete confirmation (inline two-step).
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Seed the draft once: a paired preset (layout deep-link) wins, else the
  // active theme, else the first preset.
  useEffect(() => {
    if (draft) return;
    const paired = initialPresetId ? ALL_PRESETS.find((p) => p.id === initialPresetId) : undefined;
    if (paired) {
      setDraft(clone(paired.tokens));
      setName(paired.name);
      setPresetId(paired.id);
      setSelectedId(null); // a preset becomes a NEW theme on apply
      return;
    }
    if (activeTheme) {
      setSelectedId(activeTheme.id);
      setDraft(clone(activeTheme.tokens));
      setName(activeTheme.name);
    } else if (!themesQ.isLoading) {
      const p = ALL_PRESETS[0]!;
      setDraft(clone(p.tokens));
      setName(p.name);
      setPresetId(p.id);
    }
  }, [activeTheme, themesQ.isLoading, draft, initialPresetId]);

  // Stream the draft up to an optional host whenever it changes (including the
  // initial seed). No-op when `onTokensChange` is absent (standalone Designs).
  useEffect(() => {
    if (draft) onTokensChange?.(draft);
  }, [draft, onTokensChange]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["themes", currentProjectId] });
    qc.invalidateQueries({ queryKey: ["active-theme", currentProjectId] });
  };
  const flash = (m: string) => {
    setToast(m);
    setTimeout(() => setToast(null), 3500);
  };

  const applyMut = useMutation({
    mutationFn: async () => {
      if (!draft || !currentProjectId) throw new Error("No project");
      // Editing a specific saved design — update that row in place.
      if (selectedId) {
        return themesApi.update(selectedId, { tokens: draft, name, activate: true });
      }
      // Apply-as-upsert: re-applying a preset / unsaved draft writes onto the
      // ACTIVE theme instead of spawning a new row each time (the old create()
      // path is what let dup "Glassmorphism" rows pile up). Only create a row
      // when there is no active theme yet. "Save as new" stays the explicit,
      // intentional create path.
      if (activeTheme) {
        return themesApi.update(activeTheme.id, { tokens: draft, name, activate: true });
      }
      return themesApi.create(currentProjectId, { name: name || "Custom", tokens: draft, activate: true });
    },
    onSuccess: (t: Theme) => {
      setSelectedId(t.id);
      setPresetId(null);
      invalidate();
      flash(`“${t.name}” is now live on your site.`);
    },
  });

  const saveAsMut = useMutation({
    mutationFn: async () => {
      if (!draft || !currentProjectId) throw new Error("No project");
      return themesApi.create(currentProjectId, { name: `${name || "Custom"} copy`, tokens: draft, activate: false });
    },
    onSuccess: (t: Theme) => {
      setSelectedId(t.id);
      setName(t.name);
      setPresetId(null);
      invalidate();
      flash(`Saved “${t.name}” to your designs.`);
    },
  });

  const aiMut = useMutation({
    mutationFn: async (vibe: string) => {
      const r = await aiDesign.palette({ vibe });
      if (!r.ok || !r.tokens) throw new Error(r.error || "AI palette failed");
      return r.tokens;
    },
    onSuccess: (tokens) => {
      setDraft((prev) => ({ ...(prev ?? {}), colors: { ...(prev?.colors ?? {}), ...(tokens.colors ?? {}) }, font: tokens.font ?? prev?.font }));
      setPresetId(null);
      flash("Nara suggested a palette — tweak it, then apply.");
    },
  });

  const pickPreset = (p: ThemePreset) => {
    setDraft(clone(p.tokens));
    setName(p.name);
    setSelectedId(null); // a preset becomes a NEW theme on apply
    setPresetId(p.id);
  };
  const pickExisting = (t: Theme) => {
    setDraft(clone(t.tokens));
    setName(t.name);
    setSelectedId(t.id);
    setPresetId(null);
  };

  const deleteMut = useDeleteTheme(currentProjectId);
  const removeDesign = (t: Theme) => {
    deleteMut.mutate(t.id, {
      onSuccess: () => {
        setConfirmDeleteId(null);
        // If we were editing the row we just deleted, drop back to "new draft"
        // so the next Apply upserts onto the active theme rather than a ghost.
        if (selectedId === t.id) setSelectedId(null);
        flash(`Deleted “${t.name}”.`);
      },
      onError: (err) => {
        setConfirmDeleteId(null);
        const msg =
          err instanceof ApiError && err.status === 409
            ? ((err.body as { message?: string })?.message ?? "That design is live — apply another first.")
            : "Couldn't delete that design.";
        flash(msg);
      },
    });
  };
  const setColor = (key: ColorKey, hex: string) =>
    setDraft((prev) => (prev ? { ...prev, colors: { ...(prev.colors ?? {}), [key]: hex } } : prev));
  const setFont = (role: FontRole, key: FontKey) =>
    setDraft((prev) => (prev ? { ...prev, font: { ...(prev.font ?? {}), [role]: key } } : prev));
  const setDesign = <K extends keyof DesignTokens>(key: K, value: DesignTokens[K]) =>
    setDraft((prev) => (prev ? { ...prev, design: { ...(prev.design ?? {}), [key]: value } } : prev));

  const dirty = useMemo(() => {
    if (!draft) return false;
    if (!selectedId) return true; // preset / new is always "ready to apply"
    const base = list.find((t) => t.id === selectedId);
    if (!base) return true;
    return JSON.stringify(draft) !== JSON.stringify(base.tokens) || name !== base.name;
  }, [draft, selectedId, name, list]);

  const editingActive = !!selectedId && selectedId === activeTheme?.id;
  const busy = applyMut.isPending || saveAsMut.isPending;

  if (themesQ.isLoading) {
    return (
      <Card className="grid place-items-center p-16">
        <Loader2 className="h-6 w-6 animate-spin text-subtle" strokeWidth={1.5} />
        <p className="mt-3 text-[13px] text-muted">Loading your designs…</p>
      </Card>
    );
  }
  if (!draft) {
    return <Card className="p-6 text-[13px] text-muted">No project selected.</Card>;
  }

  const controls = (
    <div className="space-y-5">
      <section>
        <p className="eyebrow mb-2 px-0.5">Start from a design</p>
        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
          {ALL_PRESETS.map((p) => (
            <PresetCard key={p.id} preset={p} selected={presetId === p.id} onPick={() => pickPreset(p)} />
          ))}
        </div>
      </section>

      {list.length > 0 && (
        <section>
          <p className="eyebrow mb-2 px-0.5">Your saved designs</p>
          <div className="flex flex-wrap gap-2">
            {list.map((t) => {
              const confirming = confirmDeleteId === t.id;
              return (
                <div
                  key={t.id}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-chip border py-1.5 pl-2.5 pr-1.5 text-[12px] transition-colors",
                    selectedId === t.id
                      ? "border-accent/40 bg-accent-soft text-accent"
                      : "border-hairline text-muted hover:border-ink/20 hover:text-ink",
                  )}
                >
                  <button
                    type="button"
                    onClick={() => pickExisting(t)}
                    className="inline-flex items-center gap-2"
                    title={`Edit ${t.name}`}
                  >
                    <Swatch colors={t.tokens.colors} size="xs" />
                    {t.name}
                    {t.is_active && (
                      <span className="text-accent" title="Active">
                        ★
                      </span>
                    )}
                  </button>
                  {/* The active design can't be deleted (it'd leave no live theme);
                      hide the control entirely so it's never an option. */}
                  {!t.is_active &&
                    (confirming ? (
                      <span className="inline-flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => removeDesign(t)}
                          disabled={deleteMut.isPending}
                          title="Confirm delete"
                          className="rounded p-0.5 text-accent hover:bg-accent/10 disabled:opacity-50"
                        >
                          {deleteMut.isPending ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2} />
                          ) : (
                            <Check className="h-3.5 w-3.5" strokeWidth={2} />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmDeleteId(null)}
                          title="Cancel"
                          className="rounded p-0.5 text-subtle hover:bg-ink/5 hover:text-ink"
                        >
                          <X className="h-3.5 w-3.5" strokeWidth={2} />
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(t.id)}
                        title="Delete design"
                        className="rounded p-0.5 text-subtle hover:bg-accent/10 hover:text-accent"
                      >
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                      </button>
                    ))}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <AiPaletteRow
        loading={aiMut.isPending}
        error={aiMut.error instanceof Error ? aiMut.error.message : null}
        onSuggest={(v) => aiMut.mutate(v)}
      />

      <section className="space-y-3">
        <p className="eyebrow px-0.5">Fine-tune colors</p>
        {GROUPS.map(({ group, fields }) => (
          <Card key={group} className="p-3">
            <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-subtle">{group}</p>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              {fields.map((f) => (
                <ColorInput
                  key={f.key}
                  label={f.label}
                  hint={f.hint}
                  value={draft.colors?.[f.key]}
                  onChange={(v) => setColor(f.key, v)}
                />
              ))}
            </div>
          </Card>
        ))}
      </section>

      <section>
        <p className="eyebrow mb-2 px-0.5">Typography</p>
        <Card className="space-y-3 p-3">
          {(["display", "body", "mono"] as FontRole[]).map((role) => (
            <FontRow key={role} role={role} value={draft.font?.[role]} onChange={(k) => setFont(role, k)} />
          ))}
        </Card>
      </section>

      <StyleControls design={draft.design} onChange={setDesign} />
    </div>
  );

  // The currently picked preset's optional LAYOUT recipe (Bento/Editorial/…).
  // When present, the design can restructure a page, not just recolor it.
  const activePreset = presetId ? ALL_PRESETS.find((p) => p.id === presetId) : undefined;
  const recipeId = activePreset?.recipe;

  const previewAndApply = (
    <div className="space-y-3">
      <BrandPreview tokens={draft} />

      <Card className="space-y-3 p-4">
        <label className="block">
          <span className="eyebrow mb-1 block">Design name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Spring launch"
            className="w-full rounded-btn border border-hairline bg-paper px-3 py-2 text-[14px] text-ink outline-none focus:border-accent/50 dark:bg-white/5"
          />
        </label>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="coral" onClick={() => applyMut.mutate()} disabled={busy || (!dirty && editingActive)}>
            {applyMut.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} />
                Applying…
              </>
            ) : (
              <>
                <Check className="h-4 w-4" strokeWidth={1.75} />
                {editingActive && !dirty ? "Live on your site" : "Apply to my site"}
              </>
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => saveAsMut.mutate()} disabled={busy}>
            {saveAsMut.isPending ? "Saving…" : "Save as new"}
          </Button>
        </div>

        {/* Layout — apply ANY structural recipe to the current page (Make only).
            Decoupled from the color preset: every layout is one click here, not
            just the one paired with the selected design (the paired one is ★). */}
        {onApplyLayout && (
          <div className="rounded-btn border border-hairline bg-paper-2/50 p-2.5 dark:bg-white/5">
            <div className="mb-2 flex items-center justify-between">
              <p className="eyebrow">Layout — restructure this page</p>
              {onUndoLayout && (
                <Button variant="ghost" size="sm" onClick={onUndoLayout} disabled={!canUndoLayout}>
                  <Undo2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                  Undo
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
              {LAYOUT_RECIPES.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => onApplyLayout(r.id)}
                  title={r.description}
                  className={cn(
                    "inline-flex items-center justify-center gap-1.5 rounded-btn border px-2 py-2 text-[12px] font-medium transition-colors",
                    r.id === recipeId
                      ? "border-accent/50 bg-accent-soft text-accent"
                      : "border-hairline text-muted hover:border-ink/25 hover:text-ink",
                  )}
                >
                  <LayoutGrid className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
                  {r.label}
                  {r.id === recipeId && <span title="Pairs with this design">★</span>}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] leading-relaxed text-subtle">
              Applies instantly to the page you're editing. Undo (or ⌘Z) to revert.
            </p>
          </div>
        )}
        {recipeId && !onApplyLayout && (
          <p className="rounded-btn border border-dashed border-hairline px-3 py-2 text-[11px] text-subtle">
            Open a page in Make to apply layouts (bento, editorial, feature grid, brutalist, stack).
          </p>
        )}
        {applyMut.error && (
          <p className="text-[12px] text-accent">
            {applyMut.error instanceof Error ? applyMut.error.message : "Couldn't apply."}
          </p>
        )}
        {saveAsMut.error && (
          <p className="text-[12px] text-accent">
            {saveAsMut.error instanceof Error ? saveAsMut.error.message : "Couldn't save."}
          </p>
        )}
        <p className="text-[11px] leading-relaxed text-subtle">
          Applies these tokens to <span className="font-medium text-muted">{current?.domain}</span> and every page
          you build — the same brand the live site renders.
        </p>
      </Card>
    </div>
  );

  const toastEl = toast ? (
    <div
      role="status"
      className="fixed bottom-28 left-1/2 z-50 -translate-x-1/2 rounded-chip bg-ink-strong px-4 py-2.5 text-[13px] font-medium text-canvas shadow-lg dark:bg-white dark:text-ink-strong"
    >
      {toast}
    </div>
  ) : null;

  if (variant === "panel") {
    return (
      <>
        <div className="space-y-5">
          {previewAndApply}
          {controls}
        </div>
        {toastEl}
      </>
    );
  }

  return (
    <>
      <div className="grid gap-5 lg:grid-cols-[1fr_minmax(0,400px)]">
        {controls}
        <div className="lg:sticky lg:top-4 lg:self-start">{previewAndApply}</div>
      </div>
      {toastEl}
    </>
  );
}

/* ------------------------------- preset card ------------------------------ */
function PresetCard({ preset, selected, onPick }: { preset: ThemePreset; selected: boolean; onPick: () => void }) {
  const c = preset.tokens.colors ?? {};
  return (
    <button
      onClick={onPick}
      title={`Use ${preset.name}`}
      className={cn(
        "group overflow-hidden rounded-card border text-left transition-all",
        selected
          ? "border-accent/50 ring-2 ring-accent/20"
          : "border-hairline hover:border-ink/20 hover:-translate-y-0.5",
      )}
    >
      {/* mini composition */}
      <div className="relative h-16 w-full" style={{ backgroundColor: c.surface ?? "#fff" }}>
        <div className="absolute left-2 top-2 h-1.5 w-10 rounded-full" style={{ backgroundColor: c.onSurface ?? "#000", opacity: 0.85 }} />
        <div className="absolute left-2 top-5 h-1 w-6 rounded-full" style={{ backgroundColor: c.onSurface ?? "#000", opacity: 0.4 }} />
        <div className="absolute bottom-2 left-2 h-4 w-10 rounded-md" style={{ backgroundColor: c.brand ?? "#000" }} />
        <div className="absolute bottom-0 right-0 h-6 w-1/3" style={{ backgroundColor: c.contrast ?? "#000" }} />
      </div>
      <div className="flex items-center justify-between gap-1 px-2.5 py-1.5">
        <span className="truncate text-[12px] font-medium text-ink">{preset.name}</span>
        {selected ? (
          <Check className="h-3.5 w-3.5 shrink-0 text-accent" strokeWidth={2.25} />
        ) : (
          <Swatch colors={c} size="xs" />
        )}
      </div>
    </button>
  );
}

function Swatch({ colors, size = "sm" }: { colors?: ThemeTokens["colors"]; size?: "xs" | "sm" }) {
  const c = colors ?? {};
  const w = size === "xs" ? "h-3.5 w-2" : "h-5 w-3";
  return (
    <span className="inline-flex overflow-hidden rounded-[3px] border border-black/10">
      {[c.surface ?? "#fff", c.brand ?? "#000", c.contrast ?? "#000"].map((col, i) => (
        <span key={i} className={w} style={{ backgroundColor: col }} />
      ))}
    </span>
  );
}

/* ------------------------------- color input ------------------------------ */
function ColorInput({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value?: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block">
      <span className="block text-[11px] font-medium text-ink">{label}</span>
      {hint && <span className="mb-0.5 block text-[10px] leading-tight text-subtle">{hint}</span>}
      <span className="mt-0.5 flex items-center gap-1.5">
        <input
          type="color"
          value={normHex(value)}
          onChange={(e) => onChange(e.target.value)}
          className="h-7 w-8 shrink-0 cursor-pointer rounded border border-hairline bg-transparent p-0"
        />
        <input
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full min-w-0 rounded-btn border border-hairline bg-paper px-2 py-1 font-mono text-[11px] text-ink outline-none focus:border-accent/50 dark:bg-white/5"
        />
      </span>
    </label>
  );
}

/* -------------------------------- font row -------------------------------- */
function FontRow({ role, value, onChange }: { role: FontRole; value?: FontKey; onChange: (k: FontKey) => void }) {
  const groups = fontsForRoleByCategory(role);
  const opts = fontsForRole(role);
  const offRole = value && !opts.some((o) => o.key === value) ? value : null;
  const [open, setOpen] = useState(false);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Lazy-load light (wght@400) previews for this role's fonts the first time it
  // opens, so each option renders in its OWN face without bulk-loading on mount.
  useEffect(() => {
    if (!open || previewLoaded) return;
    const id = `dl-font-preview-${role}`;
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = previewFontsUrl(opts.map((o) => o.key));
      document.head.appendChild(link);
    }
    setPreviewLoaded(true);
  }, [open, previewLoaded, role, opts]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = value ? FONTS[value] : undefined;
  const label = role === "display" ? "Display / headings" : role === "body" ? "Body / paragraphs" : "Mono / accents";
  const sample =
    role === "display" ? "Banking, redesigned" : role === "mono" ? "FASTER ONBOARDING" : "Apply in under a minute.";

  const pick = (k: FontKey) => {
    onChange(k);
    setOpen(false);
  };

  return (
    <div className="block" ref={ref}>
      <span className="mb-0.5 block text-[11px] font-medium text-ink">{label}</span>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 rounded-btn border border-hairline bg-paper px-2 py-1.5 text-left text-[13px] text-ink outline-none focus:border-accent/50 dark:bg-white/5"
      >
        <span className="truncate" style={{ fontFamily: current?.stack }}>
          {current ? current.label : "Default"}
        </span>
        <ChevronDown
          className={cn("h-3.5 w-3.5 shrink-0 text-subtle transition-transform", open && "rotate-180")}
          strokeWidth={1.75}
        />
      </button>

      {open && (
        <div
          className="mt-1 max-h-60 overflow-auto rounded-btn border border-hairline bg-paper shadow-lg dark:bg-ink-2"
          role="listbox"
        >
          {offRole && (
            <button
              type="button"
              onClick={() => pick(offRole)}
              className="flex w-full items-center justify-between gap-2 bg-accent-soft px-2.5 py-1.5 text-left"
            >
              <span className="truncate text-[14px] text-accent" style={{ fontFamily: FONTS[offRole].stack }}>
                {FONTS[offRole].label}
              </span>
              <Check className="h-3.5 w-3.5 shrink-0 text-accent" strokeWidth={2} />
            </button>
          )}
          {groups.map((g) => (
            <div key={g.category}>
              <p className="eyebrow px-2.5 pb-1 pt-2">{FONT_CATEGORY_LABEL[g.category]}</p>
              {g.items.map(({ key, entry }) => (
                <button
                  key={key}
                  type="button"
                  role="option"
                  aria-selected={key === value}
                  onClick={() => pick(key)}
                  className={cn(
                    "flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left transition-colors",
                    key === value ? "bg-accent-soft" : "hover:bg-paper-2 dark:hover:bg-white/5",
                  )}
                >
                  <span
                    className={cn("truncate text-[14px]", key === value ? "text-accent" : "text-ink")}
                    style={{ fontFamily: entry.stack }}
                  >
                    {entry.label}
                  </span>
                  {key === value && <Check className="h-3.5 w-3.5 shrink-0 text-accent" strokeWidth={2} />}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}

      {current && (
        <div
          className="mt-1 truncate rounded-btn border border-hairline bg-paper px-2 py-1 text-[13px] text-ink dark:bg-white/5"
          style={{ fontFamily: current.stack }}
          aria-hidden
        >
          {sample}
        </div>
      )}
    </div>
  );
}

/* ----------------------------- AI palette row ----------------------------- */
function AiPaletteRow({
  loading,
  error,
  onSuggest,
}: {
  loading: boolean;
  error: string | null;
  onSuggest: (vibe: string) => void;
}) {
  const [vibe, setVibe] = useState("modern fintech, warm and trustworthy");
  return (
    <Card className="space-y-2 p-3">
      <div className="flex items-center gap-1.5">
        <Wand2 className="h-3.5 w-3.5 text-accent" strokeWidth={1.75} />
        <p className="text-[12px] font-medium text-ink-strong">Ask Nara for a palette</p>
      </div>
      <div className="flex gap-2">
        <input
          value={vibe}
          onChange={(e) => setVibe(e.target.value)}
          placeholder="e.g. bold green, neobank energy"
          className="w-full min-w-0 rounded-btn border border-hairline bg-paper px-3 py-2 text-[13px] text-ink outline-none focus:border-accent/50 dark:bg-white/5"
        />
        <Button variant="ink" size="sm" onClick={() => vibe.trim() && onSuggest(vibe.trim())} disabled={loading || !vibe.trim()}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.75} /> : <Sparkles className="h-4 w-4" strokeWidth={1.75} />}
          Suggest
        </Button>
      </div>
      {error && <p className="text-[11px] text-accent">{error}</p>}
    </Card>
  );
}

/* ------------------------------ style controls ---------------------------- */
/** Surfaces the `design` tokens (radius / shadow / surface / fontScale /
 *  border) — the "form" of the theme, beyond color + type. Writes straight
 *  into the same draft so Apply / Save persist them. */
function StyleControls({
  design,
  onChange,
}: {
  design: DesignTokens | undefined;
  onChange: <K extends keyof DesignTokens>(key: K, value: DesignTokens[K]) => void;
}) {
  const d = { ...DESIGN_DEFAULTS, ...(design ?? {}) };
  const rpx = radiusPx(d.radius);
  return (
    <section>
      <p className="eyebrow mb-2 px-0.5">Style</p>
      <Card className="space-y-4 p-3">
        {/* radius */}
        <label className="block">
          <span className="mb-1 flex items-center justify-between text-[11px] font-medium text-ink">
            Corner radius
            <span className="font-mono text-[10px] text-subtle">{rpx}px</span>
          </span>
          <input
            type="range"
            min={0}
            max={32}
            step={1}
            value={rpx}
            onChange={(e) => onChange("radius", `${e.target.value}px`)}
            className="w-full accent-accent"
          />
        </label>

        {/* shadow */}
        <Segmented
          label="Shadow"
          value={d.shadow}
          options={[
            { value: "none", label: "None" },
            { value: "soft", label: "Soft" },
            { value: "hard", label: "Hard" },
          ]}
          onChange={(v) => onChange("shadow", v as DesignTokens["shadow"])}
        />

        {/* surface */}
        <Segmented
          label="Surface"
          value={d.surface}
          options={[
            { value: "flat", label: "Flat" },
            { value: "glass", label: "Glass" },
            { value: "gradient", label: "Gradient" },
          ]}
          onChange={(v) => onChange("surface", v as DesignTokens["surface"])}
        />

        {/* fontScale */}
        <label className="block">
          <span className="mb-1 flex items-center justify-between text-[11px] font-medium text-ink">
            Heading scale
            <span className="font-mono text-[10px] text-subtle">{d.fontScale.toFixed(2)}×</span>
          </span>
          <input
            type="range"
            min={0.85}
            max={1.5}
            step={0.05}
            value={d.fontScale}
            onChange={(e) => onChange("fontScale", Number(e.target.value))}
            className="w-full accent-accent"
          />
        </label>

        {/* border */}
        <Segmented
          label="Borders"
          value={d.border}
          options={[
            { value: "none", label: "None" },
            { value: "hairline", label: "Hairline" },
            { value: "bold", label: "Bold" },
          ]}
          onChange={(v) => onChange("border", v as DesignTokens["border"])}
        />
      </Card>
    </section>
  );
}

function Segmented({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string | undefined;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <span className="mb-1 block text-[11px] font-medium text-ink">{label}</span>
      <div className="inline-flex w-full overflow-hidden rounded-btn border border-hairline">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={cn(
              "flex-1 px-2 py-1.5 text-[12px] font-medium transition-colors",
              value === o.value
                ? "bg-accent text-white"
                : "bg-paper text-muted hover:text-ink dark:bg-white/5",
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------ brand preview ----------------------------- */
/** A realistic mini landing page rendered with the draft tokens, via the same
 *  `--dl-*` vars the live page uses (scoped to this element only). */
function BrandPreview({ tokens }: { tokens: ThemeTokens }) {
  const ref = useRef<HTMLDivElement>(null);
  const [scope, setScope] = useState<HTMLDivElement | null>(null);
  useEffect(() => setScope(ref.current), []);
  return (
    <div className="overflow-hidden rounded-card border border-hairline bg-paper-2 dark:bg-white/5">
      <div className="flex items-center justify-between border-b border-hairline px-3 py-2">
        <span className="eyebrow">Draft preview</span>
        <span className="font-mono text-[10px] text-subtle">your homepage</span>
      </div>
      <div ref={ref}>
        <ThemeStyleInjector tokens={tokens} scope={scope} />
        {/* Outer page: a soft brand-tinted backdrop so the glass surface reads. */}
        <div
          style={{
            background:
              "linear-gradient(135deg, color-mix(in srgb, var(--dl-brand, #2563eb) 16%, var(--dl-surface, #fafafa)) 0%, var(--dl-surface, #fafafa) 70%)",
            color: "var(--dl-on-surface, #0a0a0a)",
            fontFamily: "var(--dl-font-body, ui-sans-serif, system-ui, sans-serif)",
          }}
          className="p-5"
        >
          {/* Card — picks up radius / shadow / surface-fill / border tokens. */}
          <div
            style={{
              borderRadius: "var(--dl-radius, 12px)",
              background:
                "var(--dl-surface-gradient, var(--dl-surface-bg, var(--dl-surface, #ffffff)))",
              backdropFilter: "blur(var(--dl-surface-blur, 0px))",
              WebkitBackdropFilter: "blur(var(--dl-surface-blur, 0px))",
              boxShadow:
                "var(--dl-shadow, 0 1px 2px color-mix(in srgb, var(--dl-on-surface, #0a0a0a) 7%, transparent), 0 16px 40px -16px color-mix(in srgb, var(--dl-on-surface, #0a0a0a) 24%, transparent))",
              border:
                "var(--dl-border-width, 1px) solid var(--dl-border-color, color-mix(in srgb, var(--dl-on-surface, #0a0a0a) 10%, transparent))",
              color: "var(--dl-on-surface, #0a0a0a)",
            }}
            className="px-4 py-5"
          >
            <p
              className="text-[10px] font-semibold uppercase tracking-[0.14em]"
              style={{ color: "var(--dl-accent, #2563eb)", fontFamily: "var(--dl-font-mono, ui-monospace, monospace)" }}
            >
              Faster onboarding
            </p>
            <h3
              className="mt-2 font-bold leading-tight"
              style={{
                fontFamily: "var(--dl-font-display, Georgia, serif)",
                fontSize: "calc(22px * var(--dl-font-scale, 1))",
              }}
            >
              Open an account in 30 seconds.
            </h3>
            <p className="mt-2 max-w-[34ch] text-[13px] leading-relaxed" style={{ opacity: 0.8 }}>
              All digital. Decision in minutes. Spend the same day with your virtual card.
            </p>
            <div className="mt-4 flex items-center gap-2">
              <span
                className="px-4 py-2 text-[13px] font-semibold"
                style={{
                  // Mirror the live CTA: glass/gradient-aware via --dl-btn-*.
                  background: "var(--dl-btn-bg, var(--dl-brand, #2563eb))",
                  color: "var(--dl-btn-fg, var(--dl-on-brand, #fff))",
                  border: "var(--dl-btn-border, none)",
                  backdropFilter: "blur(var(--dl-btn-blur, 0px))",
                  WebkitBackdropFilter: "blur(var(--dl-btn-blur, 0px))",
                  borderRadius: "var(--dl-radius, 12px)",
                }}
              >
                Get started
              </span>
              <span
                className="px-4 py-2 text-[13px] font-medium"
                style={{
                  borderRadius: "var(--dl-radius, 12px)",
                  border:
                    "var(--dl-border-width, 1px) solid var(--dl-border-color, color-mix(in srgb, var(--dl-on-surface, #000) 18%, transparent))",
                }}
              >
                See plans
              </span>
            </div>
          </div>
        </div>
        {/* contrast band (dark footer/hero) */}
        <div
          className="flex items-center justify-between px-5 py-4"
          style={{ backgroundColor: "var(--dl-contrast, #0a0a0a)", color: "var(--dl-on-contrast, #fafafa)" }}
        >
          <span className="text-[12px] font-semibold" style={{ fontFamily: "var(--dl-font-display, Georgia, serif)" }}>
            Trusted by 1.8M+ customers
          </span>
          <span className="text-[11px]" style={{ opacity: 0.7 }}>
            4.9 / 5 ★
          </span>
        </div>
      </div>
    </div>
  );
}
